import { useEffect, useState } from "react";
import {
  PaginationParams,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import { FilterProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableFilter";
import { OrderByProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableOrderBy";
import { Dictionary } from "lodash";
import {
  loadFilterFromCookies,
  storeHistoryFilter,
} from "Utils/HistoryLocationUtils";

type HookProps<TItem, TFilter extends object> = {
  defaultOrderBy: TableIDType<TItem>;
  filters: FilterDefinition<TFilter>[];
  onDataLoad: (filter: TableFilter<TFilter>) => void;
  tableCode: string;
};

export function useTableFiltration<TItem, TFilter extends object>(
  props: HookProps<TItem, TFilter>,
) {
  const [desc, setDesc] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [orderBy, setOrderBy] = useState<TableIDType<TItem>>(
    props.defaultOrderBy,
  );

  const getDefaultValues = (): FilterValue<TFilter>[] => {
    return props.filters.map(e => {
      return {
        value: e.defaultValue,
        propertyName: e.propertyName,
      };
    });
  };

  const [filterValues, setFilterValues] = useState<FilterValue<TFilter>[]>(() =>
    getDefaultValues(),
  );

  const orderProps = {
    setOrderBy: setOrderBy,
    orderBy: orderBy,
    setDesc: setDesc,
    desc: desc,
  } as OrderByProps<TItem>;

  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    page: 0,
    rows: 50,
  });

  const getFilterObject = (): TFilter => {
    let filterObject: any = {};

    filterValues.forEach(e => {
      let definition = props.filters.find(
        f => f.propertyName === e.propertyName,
      );

      filterObject[e.propertyName] = definition?.valueMapper
        ? definition.valueMapper.fromString(e.value)
        : e.value;
    });

    return filterObject as TFilter;
  };

  const filterObject = getFilterObject();

  useEffect(() => {
    let cachedFilter = loadFilterFromCookies<TableFilter<TFilter>>(
      props.tableCode,
    );

    if (cachedFilter) {
      loadFilterDataFromHistory(cachedFilter);
    }

    setIsChecked(true);
    // eslint-disable-next-line
  }, [props.tableCode]);

  useEffect(() => {
    if (!isChecked) {
      return;
    }
    const tableFilter = {
      page: paginationParams.page,
      rows: paginationParams.rows,
      filter: filterObject,
      orderBy: orderProps.orderBy.toString(),
      desc: orderProps.desc,
    };

    props.onDataLoad(tableFilter);

    storeHistoryFilter(props.tableCode, JSON.stringify(tableFilter));

    // eslint-disable-next-line
  }, [
    filterValues,
    paginationParams.page,
    paginationParams.rows,
    orderProps.desc,
    orderProps.orderBy,
    isChecked,
  ]);

  const loadFilterDataFromHistory = (history: TableFilter<TFilter>) => {
    let newValues = filterValues.map(fv => {
      let definition = props.filters.find(
        e => e.propertyName === fv.propertyName,
      );
      let rawValue = (history.filter as any)[fv.propertyName];
      let mapped = definition?.valueMapper
        ? definition.valueMapper.toString(rawValue)
        : rawValue;

      return {
        ...fv,
        value: mapped,
      };
    });

    setFilterValues(newValues);

    orderProps.setOrderBy(history.orderBy as TableIDType<TItem>);
    orderProps.setDesc(history.desc);

    setPaginationParams({
      page: history.page,
      rows: history.rows,
    } as PaginationParams);
  };

  const setValueFactory = (propertyName: unknown) => {
    return (newValue: string | undefined) => {
      setFilterValues(
        filterValues.map(e => {
          return {
            ...e,
            value: e.propertyName === propertyName ? newValue : e.value,
          };
        }),
      );
    };
  };

  let filtrationState: FiltrationState<TItem> = {
    ordering: orderProps,
    pagination: paginationParams,
    getFilterProps: () =>
      mapToFilterProps(props.filters, filterValues, setValueFactory),
    getFilterValue: propertyName =>
      filterValues.find(e => e.propertyName === propertyName)?.value,
    onPageChange: (page, rows) => {
      setPaginationParams({ page: page, rows: rows });
    },
  };

  return [filtrationState];
}

const mapToFilterProps = <TItem>(
  filters: FilterDefinition<TItem>[],
  values: FilterValue<TItem>[],
  setValueFactory: <TItem>(
    propertyName: keyof TItem,
  ) => (newValue: string) => void,
) => {
  let filterProps: Dictionary<FilterProps> = {};

  filters.forEach(e => {
    let value = values.find(val => val.propertyName === e.propertyName);

    let filterProp: FilterProps = {
      selectboxListItems: e.listItems,
    };

    if (e.listItems) {
      filterProp.selectboxValue = value?.value;
      filterProp.setSelectboxValue = setValueFactory(e.propertyName);
    } else {
      filterProp.searchInput = value?.value;
      filterProp.setSearchInput = setValueFactory(e.propertyName);
    }

    filterProps[e.propertyName as string] = filterProp;
  });

  return filterProps;
};

export type FiltrationState<TItem> = {
  ordering: OrderByProps<TItem>;
  pagination: PaginationParams | null;
  getFilterProps: () => Dictionary<FilterProps>;
  getFilterValue: (propertyName: string) => string | undefined;
  onPageChange: (page: number, rows: number) => void;
};

type FilterValue<TItem> = {
  propertyName: keyof TItem;
  value: string | undefined;
};

type FilterDefinition<TFilter> = {
  propertyName: keyof TFilter;
  defaultValue: string | undefined;
  valueMapper?: FilterValueMapper;
  listItems?: Dictionary<string>;
};

export type FilterValueMapper = {
  fromString: (value: string | undefined) => any;
  toString: (value: any) => string | undefined;
};

export type TableFilterBase = {
  page: number;
  rows: number;
  orderBy: string;
  desc: boolean;
};

export type TableFilter<TFilter> = TableFilterBase & {
  filter: TFilter;
};
